import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from 'react'

export interface ToastOptions {
  message: string
  duration?: number
}

interface ToastContextType {
  showToast: (options: ToastOptions) => void
  hideToast: () => void
  toast: ToastOptions | null
  isVisible: boolean
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

interface ToastProviderProps {
  children: ReactNode
}

const DEFAULT_DURATION = 2000
export const ANIMATION_DURATION_MS = 300

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toast, setToast] = useState<ToastOptions | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const autoHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current)
      }
      if (clearToastTimeoutRef.current) {
        clearTimeout(clearToastTimeoutRef.current)
      }
    }
  }, [])

  const hideToast = useCallback(() => {
    setIsVisible(false)
    // Clear toast after animation completes
    if (clearToastTimeoutRef.current) {
      clearTimeout(clearToastTimeoutRef.current)
    }
    clearToastTimeoutRef.current = setTimeout(() => {
      setToast(null)
    }, ANIMATION_DURATION_MS)
  }, [])

  const showToast = useCallback(
    (options: ToastOptions) => {
      // Clear existing timeouts
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current)
      }
      if (clearToastTimeoutRef.current) {
        clearTimeout(clearToastTimeoutRef.current)
      }

      setToast(options)
      setIsVisible(true)

      // Auto-hide after duration
      const duration = options.duration ?? DEFAULT_DURATION
      autoHideTimeoutRef.current = setTimeout(() => {
        hideToast()
      }, duration)
    },
    [hideToast],
  )

  const value: ToastContextType = {
    showToast,
    hideToast,
    toast,
    isVisible,
  }

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}

export const useToast = () => {
  const context = useContext(ToastContext)

  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
